import React, {
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle
} from 'react'
import { createRoot } from 'react-dom/client'
import { Helmet } from 'react-helmet'
import './index.scss'
import cn from 'classnames'
import { useStore } from '@/hooks/storeHooks'
// 基于播放器添加的功能组件
import Watermark from './Watermark'
import Rebroadcast from './Rebroadcast'
import FastForwardFastBack from './FastForwardFastBack'
// 基于播放器添加的功能组件
import { useExternal } from 'ahooks'

/**
 * 视频播放器组件只渲染视频，不处理其他逻辑
 * TCPlayer视频播放器
 * playerdata: 播放器签名信息
 * playbackRates: 倍速
 * videoInitCallback: 视频初始化成功回调
 * isInitPlayer: 是否初始化视频播放器（根据条件选择是否渲染，比如没有购买不能看课程）
 * className: 自定义一些样式（修改视频播放器的宽高等）
 * options: 默认带一个续播功能（我觉得挺好用的，不需要的可以传空），播放器其他参数 https://cloud.tencent.com/document/product/266/63004
 * fastForwardTime: 快进/快退时间
 * addKeyUpFun: 是否添加keyup事件(包含快进、快退、音频，暂停、播放)
 * autoplay: 是否自动播放
 * isShowWatermark: 是否展示水印
 * isShowRebroadcast: 是否展示点我重播
 */
export default forwardRef(function TcVideo(
    {
        playerdata,
        playbackRates = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
        videoInitCallback,
        isInitPlayer = true,
        className,
        options = {
            plugins: {
                ContinuePlay: {
                    // 开启续播功能
                    auto: false, // [可选] 是否在视频播放后自动续播
                    text: '上次播放至 ', // [可选] 提示文案
                    btnText: '恢复播放' // [可选] 按钮文案
                }
            }
        },
        fastForwardTime = 5,
        addKeyUpFun = true,
        autoplay = true,
        isShowWatermark = false,
        isShowRebroadcast = false,
        isShowFastForwardFastBack = true
    },
    ref
) {
    const [loaded, setLoaded] = useState(false)
    const { user } = useStore()
    // 播放器dom
    const video = useRef()

    // 播放状态
    let playStatus = false
    //  播放器实例(为啥使用ref，因为在 useImperativeHandle 中取不到最新的指，useImperativeHandle是个必包，取到的值一直是第一次的值)
    let TCplayer = useRef(null)

    useImperativeHandle(ref, () => ({
        // 外部调用此方法直接显示错误 如果传入参数 则按参数显示错误信息
        loadVideoByID: (playerdata) => {
            console.log(playerdata)
            TCplayer.current.loadVideoByID({
                fileID: playerdata.fileID,
                appID: playerdata.appId,
                sign: playerdata.sign,
                t: playerdata.t,
                us: playerdata.us
            })
        },
        // 初始化点我重播
        initRebroadcast: () => {
            initRebroadcast(TCplayer.current)
        },
        // 清除点我重播
        clearRebroadcast: () => {
            if (document.getElementById('rebroadcast_dom')) {
                document.getElementById('rebroadcast_dom').remove()
            }
        },
        // 初始化播放器
        initPlayer: initPlayer,
        // 是否已经渲染过播放器
        isLoaded: loaded,
    }))
    
    // 加载sdk
    const tcPlayerReady = useExternal('https://osscdn.zhongheschool.com/ntcplayer/hls.min.0.13.and.player.v4.5.2.min.js', {
        js: {
            async: true,
        },
    });

    useEffect(() => {
         // loaded 初始化过播放器, isInitPlayer 是否初始化播放器
         if (tcPlayerReady == 'ready' && !loaded && isInitPlayer) {
            console.log(window.TCPlayer)
            initPlayer(playerdata)
        }
    }, [tcPlayerReady])

    // 视频播放添加键盘监听
    function videoAddKeyUpFun(TCplayer) {
        document.onkeyup = function (e) {
            // 目标元素只要是body 或 播放器就执行以下事件
            if (e.target == document.body || e.target == video.current) {
                var event = e || window.event
                var keyCode = event.which || event.keyCode
                var volume = TCplayer.volume()
                var currentTime = TCplayer.currentTime()

                switch (keyCode) {
                    case 32:
                        console.log('空格')

                        if (playStatus) {
                            TCplayer.pause()
                        } else {
                            TCplayer.play()
                        }

                        break
                    case 37:
                        console.log('左键')
                        TCplayer.currentTime(currentTime - fastForwardTime)
                        break
                    case 38:
                        console.log('上键')
                        if (volume == 1) return
                        volume += 0.1
                        TCplayer.volume(volume)
                        break
                    case 39:
                        console.log('右键')
                        TCplayer.currentTime(currentTime + fastForwardTime)
                        break
                    case 40:
                        console.log('下键')
                        if (volume == 0) return
                        volume -= 0.1
                        TCplayer.volume(volume)
                        break
                }
            }
        }
    }

    // 初始化水印 (使用官方的水印产品觉得不行，自己写了一个)
    function initWatermark(tcPlayer) {
        let playerDom = document.getElementById('player-container-id')
        if (!playerDom) return
        let watermarkBoxDom = document.createElement('div')
        watermarkBoxDom.id = 'watermark_canvas'
        watermarkBoxDom.style.width = '100%'
        watermarkBoxDom.style.height = '100%'
        playerDom.append(watermarkBoxDom)
        createRoot(watermarkBoxDom).render(
            <Watermark
                color="rgba(0,0,0,0.26)"
                fontSize={14}
                value={'用户' + (user?.mobile || '')}
                initWatermark={initWatermark}
                tcPlayer={tcPlayer}
            />
        )
    }

    // 点我重播初始化
    function initRebroadcast(player) {
        if (document.getElementById('rebroadcast_dom')) {
            document.getElementById('rebroadcast_dom').remove()
        }
        let playerDom = document.getElementById('player-container-id')
        let rebroadcastDom = document.createElement('div')
        rebroadcastDom.id = 'rebroadcast_dom'
        playerDom.append(rebroadcastDom)
        createRoot(rebroadcastDom).render(<Rebroadcast player={player} />)
    }

    // 快进快退初始化
    function initFastForwardFastBack(player){
        let vjsDuration = document.getElementsByClassName('vjs-duration')[0]
        let rebroadcastDom = document.createElement('div')
        rebroadcastDom.id = 'fastforward_fastback'
        vjsDuration?.after(rebroadcastDom)
        createRoot(rebroadcastDom).render(<FastForwardFastBack player={player} />)
    }

    // 初始化播放器
    function initPlayer(playerdata) {
        console.log(playerdata)
        // 点播
        TCplayer.current = TCPlayer(
            'player-container-id', // player-container-id 为播放器容器 ID，必须与 html 中一致
            {
                // TCplayer 接口详情 https://cloud.tencent.com/document/product/266/63004  功能展示：https://tcplayer.vcube.tencent.com/
                fileID: playerdata.fileID, // 请传入需要播放的视频 fileID（必须）
                appID: playerdata.appId, // 请传入点播账号的 appID（必须）
                // 私有加密播放需填写 psign， psign 即超级播放器签名，签名介绍和生成方式参见链接：https://cloud.tencent.com/document/product/266/42436
                sign: playerdata.sign,
                t: playerdata.t,
                us: playerdata.us,
                playbackRates: playbackRates, // 倍速
                autoplay,
                // definition: "", // 转码模板ID，通过这个来指定播放您想要的码率 100210
                webrtcConfig: {
                    // receiveAudio: false,  // 是否拉取音频流
                    showLog: true, // 是否在控制台打印日志
                    connectRetryCount: 3, //	SDK 与服务器重连次数
                    connectRetryDelay: 1, // SDK 与服务器重连延时
                    receiveVideo: true // 是否拉取视频流
                },
                ...options
            },
            () => {
                // 快进快退
                addKeyUpFun ? videoAddKeyUpFun(TCplayer.current) : ''

                // 水印
                isShowWatermark ? initWatermark(TCplayer.current) : ''

                // 是否展示自定义点我重播按钮
                isShowRebroadcast ? initRebroadcast(TCplayer.current) : ''

                // 初始化播放器快进快退
                isShowFastForwardFastBack ? initFastForwardFastBack(TCplayer.current) : ''
            }
        )

        console.log(videoInitCallback)

        // callback 初始化成功回调
        videoInitCallback ? videoInitCallback(TCplayer.current) : ''

        // 播放错误
        TCplayer.current.on('error', function (error) {
            console.log(error, '播放错误')
        })

        // 播放
        TCplayer.current.on('play', function (e) {
            playStatus = true
        })

        // 暂停
        TCplayer.current.on('pause', function (e) {
            playStatus = false
        })

        console.log(TCplayer.current)

        setLoaded(true)
    }

    return (
        <>
            <Helmet>
                <link
                    href="https://osscdn.zhongheschool.com/ntcplayer/tcplayer.min.css"
                    rel="stylesheet"
                />

                {/* 使用腾讯的sdk */}
                {/* <link href="https://web.sdk.qcloud.com/player/tcplayer/release/v4.6.0/tcplayer.min.css" rel="stylesheet" />
                <script src="https://web.sdk.qcloud.com/player/tcplayer/release/v4.6.0/libs/TXLivePlayer-1.2.3.min.js"></script>
                <script src="https://web.sdk.qcloud.com/player/tcplayer/release/v4.6.0/libs/hls.min.1.1.5.js"></script>
                <script src="https://web.sdk.qcloud.com/player/tcplayer/release/v4.6.0/libs/flv.min.1.6.3.js"></script>
                <script src="https://web.sdk.qcloud.com/player/tcplayer/release/v4.6.0/libs/dash.all.min.4.4.1.js"></script>
                <script src="https://web.sdk.qcloud.com/player/tcplayer/release/v4.6.0/tcplayer.v4.6.0.min.js"></script> */}

                {/* 线上sdk */}
                {/* <link async href="https://osscdn.zhongheschool.com/tcplayer/tcplayer.css" rel="stylesheet" />
                <script async src="https://osscdn.zhongheschool.com/tcplayer/videojs-ie8.js"></script>
                <script async src="https://osscdn.zhongheschool.com/tcplayer/hls.min.0.8.8.js"></script>
                <script async src="https://osscdn.zhongheschool.com/tcplayer/tcplayer.min.js"></script> */}

                {/* 这块为啥引入的是hls.min.0.13.and.player.v4.5.2.min.js , 官方给的是两个js文件？ */}
                {/* 这里因为之前引入文件时 hls.js 需要 在player.js 之前引入，但是引入文件是异步的会有报错的情况，所以把官网的js文件合成了一个，好使 */}
                {/* <script src="https://osscdn.zhongheschool.com/ntcplayer/hls.min.0.13.and.player.v4.5.2.min.js"></script> */}
            </Helmet>
            {
                // 点播
                <video
                    id="player-container-id"
                    preload="auto"
                    playsInline
                    webkit-playsinline="true"
                    className={cn(className)}
                    ref={video}
                ></video>
            }
        </>
    )
})
